import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import { CandidateDetail, JobPostingDetail } from '../types';
import { EmailFormValues } from './EmailForm';
import SendTestsModal, { SendTestsModalProps } from './SendTestsModal';
import { TestsFormValues } from './TestsForm';

interface PartialSendTestsModalProps extends Omit<SendTestsModalProps, 'helmetTitle'> {
  candidateIds: CandidateDetail['id'][];
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (
    data: void,
    variables: TestsFormValues & EmailFormValues,
    context: unknown
  ) => void | Promise<unknown>;
}

const PartialSendTestsModal = ({
  candidateIds,
  jobPostingId,
  onSuccess,
  ...props
}: PartialSendTestsModalProps) => {
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  const mutation = useAPIMutation(
    async (
      {
        testTypeList,
        message,
        attachments,
        fromName,
        subject,
        enableReplyTo,
        replyTo,
      }: TestsFormValues & EmailFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      for (const candidateId of candidateIds) {
        api.post('/email-communications/', {
          from_name: fromName,
          candidate: candidateId,
          job_posting: jobPostingId,
          subject,
          message,
          ...(enableReplyTo ? { reply_to: replyTo } : {}),
          type: 'TESTS',
          tests: testTypeList,
          attachments: attachmentIds,
        });
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/email-communications/');
      },
    }
  );

  return (
    <SendTestsModal
      title={
        candidateIds.length > 1 ? (
          <span>
            <Trans>Enviar testes para</Trans>{' '}
            <strong>
              {candidateIds.length} <Trans>candidato(s)</Trans>
            </strong>
          </span>
        ) : (
          <span>
            <Trans>Enviar testes</Trans>
          </span>
        )
      }
      helmetTitle={
        candidateIds.length > 1
          ? `${t('Enviar testes para')} ${candidateIds.length} ${t('candidato(s)')}`
          : t('Enviar testes')
      }
      jobPostingId={jobPostingId}
      onFinish={(variables) => {
        mutation.mutate(variables);
      }}
      loading={mutation.isLoading}
      {...props}
    />
  );
};

export default PartialSendTestsModal;
